/**
 * PersonalAiReport - Shared component for displaying personal AI behavioral analysis
 *
 * Used in DynamicResultPage and MemberDetailPage.
 */

import React from 'react';
import styled, { keyframes } from 'styled-components';
import { useLanguage } from '../contexts/LanguageContext';

/* ================================================================== */
/*  Keyframes                                                          */
/* ================================================================== */

const shimmer = keyframes`
  0% { background-position: -400px 0; }
  100% { background-position: 400px 0; }
`;

/* ================================================================== */
/*  Styled components                                                  */
/* ================================================================== */

const ReportContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const Section = styled.div`
  padding: 16px 20px;
  background: ${(p) => p.$bg || p.theme.colors.bgTertiary || 'rgba(255,255,255,0.03)'};
  border-left: 4px solid ${(p) => p.$accent || p.theme.colors.primary};
  border-radius: 0 ${(p) => p.theme.borderRadius?.md || '8px'} ${(p) => p.theme.borderRadius?.md || '8px'} 0;

  @media (max-width: ${(p) => p.theme.breakpoints?.mobile || '640px'}) {
    padding: 12px 14px;
  }
`;

const SectionLabel = styled.div`
  font-size: 0.78rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${(p) => p.$color || p.theme.colors.textSecondary};
  margin-bottom: 8px;
`;

const OverviewText = styled.p`
  font-size: 0.92rem;
  color: ${(p) => p.theme.colors.text};
  line-height: 1.7;
  margin: 0;
`;

const ItemList = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Item = styled.li`
  font-size: 0.88rem;
  color: ${(p) => p.theme.colors.text};
  line-height: 1.6;
  padding-left: 16px;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 10px;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: ${(p) => p.$dot || p.theme.colors.primary};
  }
`;

const RoleGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const RoleItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const RoleName = styled.span`
  font-size: 0.88rem;
  font-weight: 600;
  color: ${(p) => p.theme.colors.text};
`;

const RoleReason = styled.span`
  font-size: 0.84rem;
  color: ${(p) => p.theme.colors.textSecondary};
  line-height: 1.6;
`;

/* ---- Loading skeleton ---- */

const SkeletonBlock = styled.div`
  background: linear-gradient(
    90deg,
    ${(p) => p.theme.colors.bgTertiary || '#1a1a2e'} 25%,
    ${(p) => p.theme.colors.bgSecondary || '#16213e'} 50%,
    ${(p) => p.theme.colors.bgTertiary || '#1a1a2e'} 75%
  );
  background-size: 800px 100%;
  animation: ${shimmer} 1.6s infinite;
  border-radius: ${(p) => p.theme.borderRadius?.md || '8px'};
  height: ${(p) => p.$h || '20px'};
  width: ${(p) => p.$w || '100%'};
  margin-bottom: ${(p) => p.$mb || '0'};
`;

/* ================================================================== */
/*  Color scheme                                                       */
/* ================================================================== */

const COLORS = {
  strengths: '#10B981',
  cautions: '#F59E0B',
  roles: '#8B5CF6',
  communication: '#06B6D4',
  workStyle: '#EC4899',
  growth: '#6366F1',
};

/* ================================================================== */
/*  Component                                                          */
/* ================================================================== */

function PersonalAiReport({ data, loading }) {
  const { t } = useLanguage();

  if (loading) {
    return (
      <ReportContainer>
        <SkeletonBlock $h="60px" $mb="8px" />
        <SkeletonBlock $h="80px" $mb="8px" />
        <SkeletonBlock $h="60px" $mb="8px" />
        <SkeletonBlock $h="80px" $mb="8px" />
        <SkeletonBlock $h="50px" $mb="8px" />
        <SkeletonBlock $h="60px" />
      </ReportContainer>
    );
  }

  if (!data) return null;

  return (
    <ReportContainer>
      {/* Overview */}
      {data.overview && (
        <Section>
          <SectionLabel>{t('概要', 'Overview')}</SectionLabel>
          <OverviewText>{data.overview}</OverviewText>
        </Section>
      )}

      {/* Strengths */}
      {data.strengths && data.strengths.length > 0 && (
        <Section $accent={COLORS.strengths}>
          <SectionLabel $color={COLORS.strengths}>
            {t('強み', 'Strengths')}
          </SectionLabel>
          <ItemList>
            {data.strengths.map((s, i) => (
              <Item key={i} $dot={COLORS.strengths}>{s}</Item>
            ))}
          </ItemList>
        </Section>
      )}

      {/* Cautions */}
      {data.cautions && data.cautions.length > 0 && (
        <Section $accent={COLORS.cautions}>
          <SectionLabel $color={COLORS.cautions}>
            {t('注意点', 'Cautions')}
          </SectionLabel>
          <ItemList>
            {data.cautions.map((c, i) => (
              <Item key={i} $dot={COLORS.cautions}>{c}</Item>
            ))}
          </ItemList>
        </Section>
      )}

      {/* Suited Roles */}
      {data.suited_roles && data.suited_roles.length > 0 && (
        <Section $accent={COLORS.roles}>
          <SectionLabel $color={COLORS.roles}>
            {t('適性のある役割', 'Suited Roles')}
          </SectionLabel>
          <RoleGrid>
            {data.suited_roles.map((r, i) => (
              <RoleItem key={i}>
                <RoleName>{r.role}</RoleName>
                <RoleReason>{r.reason}</RoleReason>
              </RoleItem>
            ))}
          </RoleGrid>
        </Section>
      )}

      {/* Communication Style */}
      {data.communication_style && (
        <Section $accent={COLORS.communication}>
          <SectionLabel $color={COLORS.communication}>
            {t('コミュニケーション傾向', 'Communication Style')}
          </SectionLabel>
          <OverviewText>{data.communication_style}</OverviewText>
        </Section>
      )}

      {/* Work Style */}
      {data.work_style && (
        <Section $accent={COLORS.workStyle}>
          <SectionLabel $color={COLORS.workStyle}>
            {t('仕事の進め方', 'Work Style')}
          </SectionLabel>
          <OverviewText>{data.work_style}</OverviewText>
        </Section>
      )}

      {/* Growth Advice */}
      {data.growth_advice && data.growth_advice.length > 0 && (
        <Section $accent={COLORS.growth}>
          <SectionLabel $color={COLORS.growth}>
            {t('成長のヒント', 'Growth Advice')}
          </SectionLabel>
          <ItemList>
            {data.growth_advice.map((a, i) => (
              <Item key={i} $dot={COLORS.growth}>{a}</Item>
            ))}
          </ItemList>
        </Section>
      )}
    </ReportContainer>
  );
}

export default PersonalAiReport;
